// Lazy loader for the voice playback pill. Used by any trigger that wants to
// initiate text-to-speech playback. The pill itself owns the TtsService and UI.

export {
  buildSpeedPopover,
  formatRate,
} from '~/common/voice_playback/speed_popover'

const PILL_ELEMENT_ID = 'voice-playback-pill'
const READY_TIMEOUT_MS = 5000

export const EVENTS = {
  PLAY: 'voice-playback:play',
  STOP: 'voice-playback:stop',
  READY: 'voice-playback:ready',
  DISCONNECT: 'voice-playback:disconnect',
}

let pillReadyPromise = null

// Reset the cached promise when the pill controller disconnects (e.g. Turbo nav)
// so the next dispatchPlay re-fetches the pill HTML.
window.addEventListener(EVENTS.DISCONNECT, () => {
  pillReadyPromise = null
})

export function ensurePillLoaded() {
  if (document.getElementById(PILL_ELEMENT_ID)) return Promise.resolve()
  if (pillReadyPromise) return pillReadyPromise

  pillReadyPromise = new Promise((resolve, reject) => {
    const onReady = () => {
      window.removeEventListener(EVENTS.READY, onReady)
      resolve()
    }
    window.addEventListener(EVENTS.READY, onReady, { once: true })

    fetch('/voice_speech/pill', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok)
          throw new Error(`Pill load failed: ${response.status}`)
        return response.text()
      })
      .then((html) => {
        const wrapper = document.createElement('div')
        wrapper.innerHTML = html
        document.body.append(...wrapper.children)
      })
      .catch((error) => {
        window.removeEventListener(EVENTS.READY, onReady)
        pillReadyPromise = null
        reject(error)
      })

    setTimeout(() => {
      window.removeEventListener(EVENTS.READY, onReady)
      pillReadyPromise = null
      reject(new Error('Pill controller did not become ready in time'))
    }, READY_TIMEOUT_MS)
  })

  return pillReadyPromise
}

export async function dispatchPlay({ text, sourceId }) {
  if (!text) return
  await ensurePillLoaded()
  window.dispatchEvent(
    new CustomEvent(EVENTS.PLAY, {
      detail: { text, sourceId: sourceId || null },
    })
  )
}

export function dispatchStop() {
  window.dispatchEvent(new CustomEvent(EVENTS.STOP))
}
